import React, { useState, useEffect, useMemo } from 'react';
import { useAppDispatch } from '../store/hooks';
import {
  createFreezeAsync,
  updateFreezeAsync,
  fetchFreezeAllowanceAsync,
  type StudentSubscription,
  type FreezePreset,
  type FreezeAllowance,
} from '../store/slices/studentsSlice';
import ConfirmationModal from './ConfirmationModal';
import { formatDateEuropean } from '../utils/dateUtils';
import './FreezeModal.css';

interface FreezeModalProps {
  isOpen: boolean;
  studentId: number;
  /** When editing an existing freeze; omit to create a new one. */
  existing?: StudentSubscription | null;
  onClose: () => void;
  onSaved: () => void;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const today = () => new Date().toISOString().slice(0, 10);

function addDays(dateStr: string, n: number): string {
  return new Date(new Date(dateStr + 'T00:00:00Z').getTime() + n * DAY_MS).toISOString().slice(0, 10);
}

function inclusiveDays(from: string, to: string): number {
  return Math.round((new Date(to + 'T00:00:00Z').getTime() - new Date(from + 'T00:00:00Z').getTime()) / DAY_MS) + 1;
}

const FreezeModal: React.FC<FreezeModalProps> = ({ isOpen, studentId, existing, onClose, onSaved }) => {
  const dispatch = useAppDispatch();

  const [preset, setPreset] = useState<FreezePreset>('1m');
  const [from, setFrom] = useState<string>(today());
  const [customTo, setCustomTo] = useState<string>(addDays(today(), 29));
  const [allowance, setAllowance] = useState<FreezeAllowance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Initialize from the existing freeze (edit) or defaults (create) when the modal transitions
  // to open. Done during render (not in an effect) per the codebase's adjust-state-on-prop-change
  // pattern — see LessonEditor's vacation sync.
  const [prevOpen, setPrevOpen] = useState(false);
  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    if (isOpen) {
      setError(null);
      setAllowance(null);
      if (existing) {
        const start = existing.startDate.slice(0, 10);
        const end = (existing.effectiveEndDate ?? existing.endDate ?? existing.estimatedEndDate ?? start).slice(0, 10);
        setFrom(start);
        setCustomTo(end);
        const days = inclusiveDays(start, end);
        setPreset(days === 30 ? '1m' : days === 60 ? '2m' : 'custom');
      } else {
        setFrom(today());
        setCustomTo(addDays(today(), 29));
        setPreset('1m');
      }
    }
  }

  // Locally-derived end date + price (snappy); the server confirms the authoritative values.
  const computedTo = useMemo(() => {
    if (preset === '1m') return addDays(from, 29);
    if (preset === '2m') return addDays(from, 59);
    return customTo;
  }, [preset, from, customTo]);

  const days = useMemo(
    () => (computedTo >= from ? inclusiveDays(from, computedTo) : 0),
    [from, computedTo]
  );
  const price = useMemo(() => parseFloat((days * 1).toFixed(2)), [days]);

  // Live allowance (days used this year) from the server, debounced on input changes.
  useEffect(() => {
    if (!isOpen || !from || computedTo < from) return;
    const excludeSubId = existing?.id;
    const t = setTimeout(() => {
      dispatch(fetchFreezeAllowanceAsync({
        studentId,
        from,
        preset: preset === 'custom' ? undefined : preset,
        to: preset === 'custom' ? customTo : undefined,
        excludeSubId,
      }))
        .unwrap()
        .then(setAllowance)
        .catch(() => { /* non-fatal: allowance line just won't render */ });
    }, 250);
    return () => clearTimeout(t);
  }, [isOpen, dispatch, studentId, from, preset, customTo, computedTo, existing?.id]);

  const overCap = !!allowance && allowance.remainingDays <= 0 && days > 0 &&
    (allowance.usedDays + days > allowance.maxDays);

  const handleSave = async () => {
    setError(null);
    if (computedTo < from) {
      setError('End date must be on or after the start date.');
      return;
    }
    setSaving(true);
    try {
      const data = preset === 'custom'
        ? { from, to: customTo }
        : { from, preset };
      if (existing) {
        await dispatch(updateFreezeAsync({ studentId, subscriptionId: existing.id, data })).unwrap();
      } else {
        await dispatch(createFreezeAsync({ studentId, data })).unwrap();
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to save freeze');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ConfirmationModal
      isOpen={isOpen}
      title={existing ? 'Edit freeze' : 'Freeze subscription'}
      message="A freeze is a paid pause (30 BYN/month). The student is grayed out and absences aren't counted for the period."
      onConfirm={handleSave}
      onCancel={onClose}
      confirmText={saving ? 'Saving…' : (existing ? 'Save freeze' : 'Freeze')}
      confirmDisabled={saving || days <= 0 || overCap}
    >
      <div className="freeze-modal">
        <div className="freeze-modal__field">
          <span className="freeze-modal__label">Duration</span>
          <div className="freeze-segments" role="group" aria-label="Freeze duration">
            {([['1m', '1 month'], ['2m', '2 months'], ['custom', 'Custom']] as [FreezePreset, string][]).map(
              ([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`freeze-segments__btn ${preset === value ? 'is-active' : ''}`}
                  onClick={() => setPreset(value)}
                >
                  {label}
                </button>
              )
            )}
          </div>
        </div>

        <div className="freeze-modal__dates">
          <div className="freeze-modal__field">
            <label className="freeze-modal__label" htmlFor="freeze-from">From</label>
            <input
              id="freeze-from"
              type="date"
              className="form-input"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="freeze-modal__field">
            <label className="freeze-modal__label" htmlFor="freeze-to">To</label>
            <input
              id="freeze-to"
              type="date"
              className="form-input"
              value={computedTo}
              min={from}
              disabled={preset !== 'custom'}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </div>
        </div>

        <div className="freeze-modal__summary">
          <span>Period: <strong>{formatDateEuropean(from)} — {formatDateEuropean(computedTo)}</strong></span>
          <span>Days: <strong>{days > 0 ? days : '—'}</strong></span>
          <span>Price: <strong>{price.toFixed(2)} BYN</strong></span>
        </div>

        {allowance && (
          <div className={`freeze-modal__summary ${overCap ? 'freeze-modal__allowance--over' : ''}`}>
            <span>
              Freeze used in {allowance.year}: <strong>{allowance.usedDays} / {allowance.maxDays} days</strong>
              {days > 0 && ` (+${days} requested)`}
            </span>
            {overCap && <span><strong>Over the {allowance.maxDays}-day yearly limit</strong></span>}
          </div>
        )}

        {error && <span className="error-text">{error}</span>}
      </div>
    </ConfirmationModal>
  );
};

export default FreezeModal;
